import React, { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, Send, ImagePlus, X, Award, Image as ImageIcon, MessageSquare, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import api, { timeAgo, fileToBase64, prettyDate, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";

const REACTIONS = [
  { key: "pray", emoji: "🙏", label: "Pray" },
  { key: "heart", emoji: "❤️", label: "Love" },
  { key: "strength", emoji: "💪", label: "Strength" },
  { key: "star", emoji: "⭐", label: "Star" },
];
const R_MAP = Object.fromEntries(REACTIONS.map((r) => [r.key, r]));

/* ---------------- Comments ---------------- */
const CommentsSection = ({ post, expanded, setExpanded, countState }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [count, setCount] = countState;
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/community-v2/comments/${post.id}?post_type=${post.kind}`);
      setComments(data.comments || []);
      setCount(data.count ?? (data.comments || []).length);
    } catch (e) { setComments([]); }
    finally { setLoading(false); }
  }, [post.id, post.kind, setCount]);

  useEffect(() => { if (expanded && comments.length === 0) load(); }, [expanded, load, comments.length]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const body = text.trim();
    setText("");
    try {
      await api.post("/community-v2/comments", { post_id: post.id, post_type: post.kind, message: body });
      setExpanded(true);
      await load();
    } catch (err) { toast.error(errMsg(err, "Could not comment")); setText(body); }
  };

  return (
    <div>
      {expanded && (
        <div className="px-4 pt-2 space-y-3">
          {loading ? (
            <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
          ) : comments.map((c) => (
            <div key={c.id} className="flex gap-2">
              <Avatar name={c.anonymous_name} src={c.avatar_base64} size={32} />
              <div className="bg-secondary/60 rounded-2xl px-3 py-2 max-w-[85%]">
                <p className="text-xs font-semibold">{c.anonymous_name}</p>
                <p className="text-sm text-foreground/90">{c.message}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(c.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="flex items-center gap-2 px-4 py-3">
        <Avatar name={user?.name} src={user?.avatar_base64} size={32} />
        <input data-testid="comment-input" ref={inputRef} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Write a comment…"
          className="flex-1 px-4 py-2 text-sm rounded-full bg-secondary/60 border border-transparent focus:border-primary outline-none" />
        <button type="submit" disabled={!text.trim()} className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 disabled:opacity-40">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};

/* ---------------- Post card ---------------- */
const PostCard = ({ post }) => {
  const [counts, setCounts] = useState(post.reactions || {});
  const [mine, setMine] = useState(post.my_reactions || []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const commentCountState = useState(post.comment_count || 0);
  const [commentCount] = commentCountState;

  const react = async (emoji) => {
    setPickerOpen(false);
    try {
      const { data } = await api.post("/community-v2/reactions/toggle", { post_id: post.id, post_type: post.kind, emoji });
      if (data.counts) setCounts(data.counts);
      setMine((m) => (data.action === "added" ? [...new Set([...m, emoji])] : m.filter((x) => x !== emoji)));
    } catch (e) { toast.error(errMsg(e, "Could not react")); }
  };

  const total = REACTIONS.reduce((s, r) => s + (counts[r.key] || 0), 0);
  const present = REACTIONS.filter((r) => (counts[r.key] || 0) > 0);
  const myPrimary = mine.length ? R_MAP[mine[0]] : null;
  const subtitle = post.kind === "reflection" ? "shared a reflection" : post.kind === "milestone" ? "reached a milestone" : "posted";

  return (
    <div data-testid="community-post" className="bg-card border border-border rounded-2xl shadow-sm animate-fade-up overflow-hidden">
      {/* header */}
      <div className="flex items-center gap-3 p-4 pb-3">
        <Avatar name={post.anonymous_name} src={post.avatar_base64} size={44} />
        <div className="min-w-0">
          <p className="font-semibold truncate flex items-center gap-1.5">
            {post.anonymous_name}
            {post.kind === "milestone" && <Award className="w-4 h-4 text-brass" />}
          </p>
          <p className="text-xs text-muted-foreground">
            <span className="capitalize">{subtitle}</span>
            {post.date ? ` · ${prettyDate(post.date)}` : ""} · {timeAgo(post.created_at)}
          </p>
        </div>
      </div>

      {/* body */}
      <div className="px-4 pb-3">
        {post.title && post.kind === "milestone" && <p className="font-heading text-lg text-brass mb-1">{post.title}</p>}
        <p className="text-foreground/90 leading-relaxed whitespace-pre-wrap">{post.message}</p>
      </div>
      {post.photo_base64 && <img src={post.photo_base64} alt="attachment" className="w-full max-h-[520px] object-cover" />}

      {/* summary line */}
      {(total > 0 || commentCount > 0) && (
        <div className="flex items-center justify-between px-4 py-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            {present.length > 0 && (
              <span className="flex -space-x-1">
                {present.slice(0, 3).map((r) => <span key={r.key} className="text-[13px]">{r.emoji}</span>)}
              </span>
            )}
            {total > 0 && <span className="ml-1">{total}</span>}
          </div>
          {commentCount > 0 && (
            <button onClick={() => setExpanded((v) => !v)} className="hover:underline">
              {commentCount} {commentCount === 1 ? "comment" : "comments"}
            </button>
          )}
        </div>
      )}

      {/* action bar */}
      <div className="mx-4 border-t border-border grid grid-cols-2 relative">
        <button
          data-testid="react-button"
          onClick={() => (myPrimary ? react(myPrimary.key) : setPickerOpen((v) => !v))}
          onMouseEnter={() => setPickerOpen(true)}
          className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors hover:bg-secondary ${myPrimary ? "text-primary" : "text-muted-foreground"}`}
        >
          {myPrimary ? <span className="text-base leading-none">{myPrimary.emoji}</span> : <ThumbsUp className="w-4 h-4" />}
          {myPrimary ? myPrimary.label : "React"}
        </button>
        <button
          data-testid="comment-toggle"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary transition-colors"
        >
          <MessageSquare className="w-4 h-4" /> Comment
        </button>

        {pickerOpen && (
          <div
            onMouseLeave={() => setPickerOpen(false)}
            className="absolute -top-12 left-0 flex items-center gap-1 bg-popover border border-border rounded-full px-2 py-1.5 shadow-xl z-10 animate-fade-up"
          >
            {REACTIONS.map((r) => (
              <button key={r.key} data-testid={`react-${r.key}`} onClick={() => react(r.key)} title={r.label}
                className={`w-9 h-9 rounded-full flex items-center justify-center text-lg hover:scale-125 transition-transform ${mine.includes(r.key) ? "bg-primary/20" : ""}`}>
                {r.emoji}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-border mt-1">
        <CommentsSection post={post} expanded={expanded} setExpanded={setExpanded} countState={commentCountState} />
      </div>
    </div>
  );
};

/* ---------------- Composer ---------------- */
const Composer = ({ onPosted, onOpenMilestone }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");
  const [photo, setPhoto] = useState("");
  const [posting, setPosting] = useState(false);
  const fileRef = useRef(null);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Image too large (max 4MB)"); return; }
    try { const b64 = await fileToBase64(f); setPhoto(b64); setOpen(true); } catch { toast.error("Could not read image"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setPosting(true);
    try {
      await api.post("/community-v2/posts", { content: content.trim(), photo_base64: photo || null, kind: "text" });
      setContent(""); setPhoto(""); setOpen(false);
      toast.success("Posted to the community");
      onPosted();
    } catch (err) { toast.error(errMsg(err, "Could not post")); }
    finally { setPosting(false); }
  };

  const firstName = (user?.name || "").split(" ")[0] || "there";

  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm mb-6">
      <div className="flex items-center gap-3 p-4">
        <Avatar name={user?.name} src={user?.avatar_base64} size={42} />
        {!open ? (
          <button data-testid="open-composer" onClick={() => setOpen(true)}
            className="flex-1 text-left px-4 py-2.5 rounded-full bg-secondary/60 text-muted-foreground hover:bg-secondary transition-colors">
            What's on your heart, {firstName}?
          </button>
        ) : (
          <span className="font-heading text-lg font-semibold">Create a post</span>
        )}
      </div>

      {open && (
        <form onSubmit={submit} className="px-4 pb-4">
          <textarea data-testid="post-content-input" autoFocus value={content} onChange={(e) => setContent(e.target.value)} rows={3}
            placeholder={`What's on your heart, ${firstName}?`}
            className="w-full px-1 py-1 bg-transparent outline-none resize-none text-lg placeholder:text-muted-foreground/70" />
          {photo && (
            <div className="relative inline-block mt-2">
              <img src={photo} alt="preview" className="max-h-52 rounded-xl border border-border" />
              <button type="button" onClick={() => setPhoto("")} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <div className="flex items-center justify-between mt-3 gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground px-3 py-1.5 rounded-lg hover:bg-secondary">
              <ImagePlus className="w-4 h-4 text-seafoam" /> Photo
              <input data-testid="post-photo-input" ref={fileRef} type="file" accept="image/*" onChange={pick} className="hidden" />
            </button>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => { setOpen(false); setContent(""); setPhoto(""); }} className="text-sm px-4 py-2 rounded-full hover:bg-secondary">Cancel</button>
              <button data-testid="post-submit-button" type="submit" disabled={posting || !content.trim()}
                className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-5 py-2 rounded-full hover:bg-atlantic active:scale-95 transition-all disabled:opacity-50">
                {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post
              </button>
            </div>
          </div>
        </form>
      )}

      {!open && (
        <div className="grid grid-cols-2 border-t border-border">
          <button onClick={() => fileRef.current?.click()} className="flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-muted-foreground hover:bg-secondary transition-colors">
            <ImageIcon className="w-4 h-4 text-seafoam" /> Photo
            <input data-testid="composer-photo-input-collapsed" ref={fileRef} type="file" accept="image/*" onChange={pick} className="hidden" />
          </button>
          <button data-testid="open-milestone" onClick={onOpenMilestone} className="flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-muted-foreground hover:bg-secondary transition-colors border-l border-border">
            <Award className="w-4 h-4 text-brass" /> Milestone
          </button>
        </div>
      )}
    </div>
  );
};

/* ---------------- Milestone dialog ---------------- */
const MilestoneDialog = ({ onClose, onCreated }) => {
  const [title, setTitle] = useState("");
  const [days, setDays] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.post("/community-v2/milestones", { title: title.trim(), days: days ? parseInt(days, 10) : null, message: message.trim() || null });
      toast.success("Milestone shared 🎉");
      onCreated(); onClose();
    } catch (err) { toast.error(errMsg(err, "Could not share milestone")); }
    finally { setBusy(false); }
  };
  const inp = "w-full px-4 py-2.5 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-nightsea/70 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md bg-card border border-border rounded-2xl p-6 animate-fade-up">
        <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2"><Award className="w-5 h-5 text-brass" /> Share a milestone</h3>
        <div className="space-y-3">
          <input data-testid="milestone-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. 30 days sober)" className={inp} />
          <input data-testid="milestone-days" type="number" value={days} onChange={(e) => setDays(e.target.value)} placeholder="Days (optional)" className={inp} />
          <textarea data-testid="milestone-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="A word about this milestone (optional)" className={inp + " resize-none"} />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm border border-border hover:bg-secondary">Cancel</button>
          <button data-testid="milestone-submit" type="submit" disabled={busy || !title.trim()} className="px-5 py-2 rounded-full text-sm font-semibold bg-brass text-accent-foreground disabled:opacity-50">
            {busy ? "Sharing…" : "Share"}
          </button>
        </div>
      </form>
    </div>
  );
};

/* ---------------- Page ---------------- */
const Community = () => {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState([]);
  const [showMilestone, setShowMilestone] = useState(false);

  const load = useCallback(async () => {
    try {
      const [feedRes, onlineRes] = await Promise.allSettled([
        api.get("/community-v2/feed/combined"),
        api.get("/dm/presence/online-friends"),
      ]);
      if (feedRes.status === "fulfilled") setFeed(feedRes.value.data.feed || []);
      if (onlineRes.status === "fulfilled") setOnline(onlineRes.value.data.online || []);
    } catch (e) { toast.error("Could not load the community"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="px-4 sm:px-6 py-6">
      <div className="max-w-xl mx-auto">
        {/* stories / online row */}
        {online.length > 0 && (
          <div className="mb-5 bg-card border border-border rounded-2xl p-3" data-testid="online-friends">
            <div className="flex gap-4 overflow-x-auto">
              {online.map((f, i) => (
                <div key={f.id || i} className="flex flex-col items-center gap-1 shrink-0 w-16">
                  <Avatar name={f.display_name || f.name} src={f.avatar_base64} size={52} ring />
                  <span className="text-xs text-muted-foreground truncate w-full text-center">{f.display_name || f.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <Composer onPosted={load} onOpenMilestone={() => setShowMilestone(true)} />

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : feed.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">No posts yet. Be the first to share.</p>
        ) : (
          <div className="space-y-5" data-testid="community-feed">
            {feed.map((p) => <PostCard key={`${p.kind}-${p.id}`} post={p} />)}
          </div>
        )}
      </div>
      {showMilestone && <MilestoneDialog onClose={() => setShowMilestone(false)} onCreated={load} />}
    </div>
  );
};

export default Community;
